/**
 * Blur Placeholder Utility for 404 Page Background
 *
 * LQIP data URLs for the full-bleed 404 hero (same approach as spa:
 * `spaBlurPlaceholders.ts`). Placeholders are 40×40 WebP, base64 data URLs,
 * with blur applied in the generator.
 *
 * Source assets: `public/images/404 page/404-bg_pc.webp`,
 * `404-bg_mobile.webp` (from JPEG masters via cwebp).
 */

const notFoundBlurPlaceholderMap: Record<string, string> = {
  "/images/404 page/404-bg_pc.webp":
    "data:image/webp;base64,UklGRqQAAABXRUJQVlA4IJgAAADQBgCdASooACgAPzmIulQvKSWjMdM8AeAnCUAXwYXKF6AO6W94C2RufYg8OjU5Mqd9+6DbE1xNxQ1BUfSIFQAA/q5RFyfOFV5UBFzuYdD1z0pEIrTJKjg243iqISsOoET1pV1AJJmYP22y3Jaq0SokRdbvAMacWL8NvzQL3hRUa0ajfEp5WX2OmuvgbXHa6U318jl2t0AAAA==",
  "/images/404 page/404-bg_mobile.webp":
    "data:image/webp;base64,UklGRrAAAABXRUJQVlA4IKQAAAAwBgCdASooACgAPzF+t1OuqCWitVgKqdAmCWIAvsNT2Oqk8H2oG0pnuWGqOrQKagYlJYhGTawC7CAMANZeDc2MmbnMOO2KbgrkDqbvavCCXB4OG81SHJbGMKEgOrCZhIn/wCKWnr9mSKoFBDjMNn632SMTPy4N5XFaUEDe7oi1lINVhcGMiFYOKkyAHsZeR/5tJcaU+iF5XSGindnXNeo3uAAAAA==",
};

/**
 * Blur placeholder for a 404 background path (O(1) map + `_pc` / `_mobile` fallbacks).
 */
export function getNotFoundBlurPlaceholder(
  imagePath: string,
): string | undefined {
  if (notFoundBlurPlaceholderMap[imagePath]) {
    return notFoundBlurPlaceholderMap[imagePath];
  }

  const pathWithoutLeadingSlash = imagePath.startsWith("/")
    ? imagePath.slice(1)
    : imagePath;
  if (notFoundBlurPlaceholderMap[pathWithoutLeadingSlash]) {
    return notFoundBlurPlaceholderMap[pathWithoutLeadingSlash];
  }

  const pathWithLeadingSlash = imagePath.startsWith("/")
    ? imagePath
    : `/${imagePath}`;
  if (notFoundBlurPlaceholderMap[pathWithLeadingSlash]) {
    return notFoundBlurPlaceholderMap[pathWithLeadingSlash];
  }

  if (imagePath.includes("_mobile.webp")) {
    const pcVariant = imagePath.replace("_mobile.webp", "_pc.webp");
    if (notFoundBlurPlaceholderMap[pcVariant]) {
      return notFoundBlurPlaceholderMap[pcVariant];
    }
  }

  if (imagePath.includes("_pc.webp")) {
    const mobileVariant = imagePath.replace("_pc.webp", "_mobile.webp");
    if (notFoundBlurPlaceholderMap[mobileVariant]) {
      return notFoundBlurPlaceholderMap[mobileVariant];
    }
  }

  return undefined;
}

export function setNotFoundBlurPlaceholder(
  imagePath: string,
  dataURL: string,
): void {
  notFoundBlurPlaceholderMap[imagePath] = dataURL;
}

export function getAllNotFoundBlurPlaceholders(): Record<string, string> {
  return { ...notFoundBlurPlaceholderMap };
}
